import { useEffect, useState } from 'react';
import { CommentBubbleIcon, SkeletonCard, SplitBar } from './common';
import { findUserProfile, formatRelativeTime, getCommentLabel, getPostSplit, getPostTypeOption, getTopicLabel, getTopicStyle, isOpenThought, userOwnsPost } from '../utils/hixrs';
import { getTotalComments } from '../utils/threads';

const HOT_FILTERS = [
  { id: 'hot', label: 'Hot' },
  { id: 'split', label: 'Split' },
  { id: 'heated', label: 'Heated' },
  { id: 'discussed', label: 'Discussed' }
];

const DEFAULT_THOUGHT_ACCENT = '#28D7FF';

function rgbaFromHex(hex, alpha = 0.18) {
  const clean = (hex || '').replace('#', '');
  if (clean.length !== 6) return `rgba(40, 215, 255, ${alpha})`;
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function rgbToHex(r, g, b) {
  return `#${[r, g, b].map(value => Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, '0')).join('')}`;
}

function useImageAccent(src, enabled) {
  const [accent, setAccent] = useState(DEFAULT_THOUGHT_ACCENT);

  useEffect(() => {
    if (!enabled || !src) {
      setAccent(DEFAULT_THOUGHT_ACCENT);
      return;
    }

    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (cancelled) return;
      try {
        const canvas = document.createElement('canvas');
        const size = 24;
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, size, size);
        const { data } = ctx.getImageData(0, 0, size, size);
        let r = 0;
        let g = 0;
        let b = 0;
        let weightTotal = 0;

        for (let i = 0; i < data.length; i += 4) {
          const red = data[i];
          const green = data[i + 1];
          const blue = data[i + 2];
          const max = Math.max(red, green, blue);
          const min = Math.min(red, green, blue);
          const saturation = max - min;
          const brightness = (red + green + blue) / 3;
          if (brightness < 28 || brightness > 238) continue;
          const weight = Math.max(1, saturation);
          r += red * weight;
          g += green * weight;
          b += blue * weight;
          weightTotal += weight;
        }

        if (weightTotal > 0) {
          setAccent(rgbToHex(r / weightTotal, g / weightTotal, b / weightTotal));
        } else {
          setAccent(DEFAULT_THOUGHT_ACCENT);
        }
      } catch (error) {
        setAccent(DEFAULT_THOUGHT_ACCENT);
      }
    };
    img.onerror = () => !cancelled && setAccent(DEFAULT_THOUGHT_ACCENT);
    img.src = src;

    return () => {
      cancelled = true;
    };
  }, [src, enabled]);

  return accent;
}

function getHotScore(post) {
  const lockIns = Number(post.totalVotes || 0);
  const comments = getTotalComments(post.comments || []);
  const heatTaps = Number(post.heatCount || 0);
  const flips = Number(post.flipCount || 0);
  const split = getPostSplit(post);
  const splitHeat = lockIns > 0 ? Math.max(0, 100 - Math.abs(split.pctA - 50) * 2) : 0;
  const confidence = Math.min(lockIns / 12, 1);

  return Math.round(
    lockIns * 5 +
    comments * 9 +
    heatTaps * 7 +
    flips * 6 +
    splitHeat * confidence
  );
}

function getSplitScore(post) {
  const lockIns = Number(post.totalVotes || 0);
  if (!lockIns) return 0;
  return Math.max(0, 100 - Math.abs(getPostSplit(post).pctA - 50) * 2);
}

function getHotReason(post, mode = 'hot') {
  const comments = getTotalComments(post.comments || []);
  const heatTaps = Number(post.heatCount || 0);
  const splitScore = getSplitScore(post);

  if (mode === 'split') return 'Most Split';
  if (mode === 'heated') return 'Heated';
  if (mode === 'discussed') return 'Discussed';
  if (comments >= heatTaps && comments >= 3) return 'Discussed';
  if (heatTaps >= 3) return 'Heated';
  if (splitScore >= 80) return 'Most Split';
  return 'Rising';
}

function getHotReasonClass(reason) {
  return reason.toLowerCase().replace(/\s+/g, '-');
}

function sortHotPosts(posts, mode) {
  return [...posts].sort((a, b) => {
    if (mode === 'split') {
      return getSplitScore(b) - getSplitScore(a) ||
        Number(b.totalVotes || 0) - Number(a.totalVotes || 0);
    }

    if (mode === 'heated') {
      return Number(b.heatCount || 0) - Number(a.heatCount || 0) ||
        getHotScore(b) - getHotScore(a);
    }

    if (mode === 'discussed') {
      return getTotalComments(b.comments || []) - getTotalComments(a.comments || []) ||
        getHotScore(b) - getHotScore(a);
    }

    return getHotScore(b) - getHotScore(a);
  });
}

export function PostCard({ post, userVote, onVote, onOpen, requireLogin, user, profile, users = [], onHeat, onReport, onPostDelete, onProfileOpen, isLocking = false }) {
  const voted = userVote;
  const { pctA, pctB } = getPostSplit(post);
  const userPct = voted === 'a' ? pctA : pctB;
  const isMajority = voted === 'a' ? pctA >= pctB : pctB >= pctA;
  const tc = getTopicStyle(post.topic);
  const topicLabel = getTopicLabel(post.topic);
  const heated = Boolean(user && post.heatedBy?.includes(user.uid));
  const heatCount = post.heatCount || 0;
  const postTypeOption = getPostTypeOption(post.postType);
  const thought = isOpenThought(post);
  const thoughtAccent = useImageAccent(post.image, thought);
  const thoughtStyle = {
    '--thought-accent': thoughtAccent,
    '--thought-accent-soft': rgbaFromHex(thoughtAccent, 0.16),
    '--thought-accent-shadow': rgbaFromHex(thoughtAccent, 0.28)
  };
  const isPostOwner = userOwnsPost(post, user, profile);
  const commentTotal = getTotalComments(post.comments || []);
  const liveAuthor = findUserProfile(users, post.authorId);
  const authorName = liveAuthor?.displayName || post.authorName || 'Anonymous';
  const authorPhoto = liveAuthor?.photoURL || post.authorPhoto || '';
  const authorHandle = liveAuthor?.username || post.authorHandle || '';
  const authorInitial = authorName?.[0]?.toUpperCase() || 'H';
  const authorProfile = { uid: post.authorId, displayName: authorName, username: authorHandle, photoURL: authorPhoto };
  const postTime = formatRelativeTime(post.createdAt);

  if (thought) {
    return (
      <div className={`post-card open-thought-card hix-theme-thought ${post.image ? 'has-post-image' : ''}`} style={thoughtStyle} onClick={() => onOpen(post)}>
        <div className="post-author-row">
          <button type="button" className="post-author profile-trigger" onClick={(e) => { e.stopPropagation(); onProfileOpen(authorProfile); }}>
            <div className="post-author-avatar">
              {authorPhoto ? <img src={authorPhoto} alt="author" /> : authorInitial}
            </div>
            <div className="post-author-copy">
              <strong>{authorName}</strong>
              <div className="post-author-meta">
                <span>{authorHandle ? `@${authorHandle}` : 'Open thought'}</span>
                <span className="post-time">{postTime}</span>
                <span className="view-profile-pill">View profile</span>
              </div>
            </div>
          </button>
          <div className="post-author-actions">
            <button className="report-link" onClick={(e) => { e.stopPropagation(); requireLogin(() => onReport({ type: 'post', postId: post.id, preview: post.question })); }}>
              Report
            </button>
          </div>
        </div>
        <div className="open-thought-top">
          <span className="open-thought-label">Open Thought</span>
          <span className="post-topic thought-topic" style={{ background: tc.bg, color: tc.color }}>{topicLabel}</span>
        </div>
        <div className="open-thought-text">{post.question}</div>
        {post.image && <img className="open-thought-image" src={post.image} alt="Open Thought attachment" loading="lazy" />}
        {isPostOwner && (
          <div className="post-owner-tools thought-owner-tools" onClick={e => e.stopPropagation()}>
            <span>Your thought</span>
            <div className="post-owner-buttons">
              <button type="button" className="danger" onClick={() => requireLogin(() => onPostDelete(post))}>Delete</button>
            </div>
          </div>
        )}
        <div className="comment-strip open-thought-actions">
          <button
            className={`heat-action ${heated ? 'heated' : ''}`}
            onClick={(e) => { e.stopPropagation(); requireLogin(() => onHeat(post.id)); }}
            aria-label={heated ? 'Remove heat from this thought' : 'Heat this thought'}
            title={heated ? 'Remove heat' : 'Heat this thought'}>
            <span className="heat-fire">&#128293;</span><span className="heat-label">Heat</span><strong>{heatCount}</strong>
          </button>
          <button className="comment-open-btn" onClick={(e) => { e.stopPropagation(); onOpen(post); }} aria-label={`Open ${commentTotal} ${getCommentLabel(commentTotal)}`} title={`${commentTotal} ${getCommentLabel(commentTotal)}`}>
            <CommentBubbleIcon />
            <strong>{commentTotal}</strong>
            <span className="comment-button-word">{getCommentLabel(commentTotal)}</span>
          </button>
          <button className="suggest-hix-btn" onClick={(e) => { e.stopPropagation(); onOpen(post); }}>
            Suggest Hix
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`post-card hix-theme-${postTypeOption.key} ${post.image ? 'has-post-image' : ''}`} onClick={() => onOpen(post)}>
      <div className="post-author-row">
        <button type="button" className="post-author profile-trigger" onClick={(e) => { e.stopPropagation(); onProfileOpen(authorProfile); }}>
          <div className="post-author-avatar">
            {authorPhoto ? <img src={authorPhoto} alt="author" /> : authorInitial}
          </div>
          <div className="post-author-copy">
            <strong>{authorName}</strong>
            <div className="post-author-meta">
              <span>{authorHandle ? `@${authorHandle}` : 'Hix creator'}</span>
              <span className="post-time">{postTime}</span>
              <span className="view-profile-pill">View profile</span>
            </div>
          </div>
        </button>
        <div className="post-author-actions">
          <button className="report-link" onClick={(e) => { e.stopPropagation(); requireLogin(() => onReport({ type: 'post', postId: post.id, preview: post.question })); }}>
            Report
          </button>
        </div>
      </div>
      <div className="post-top">
        <div className="post-meta-row">
          <span className="post-topic" style={{ background: tc.bg, color: tc.color }}>{topicLabel}</span>
          <span className={`post-type-badge type-${postTypeOption.key}`}>{postTypeOption.label}</span>
        </div>
        <span className="post-heat">Split {post.heat || 50}%</span>
      </div>
      <div className="post-question">{post.question}</div>
      {post.image && <img className="post-image" src={post.image} alt="Hix attachment" loading="lazy" />}
      {isPostOwner && (
        <div className="post-owner-tools" onClick={e => e.stopPropagation()}>
          <span>Your Hix</span>
          <div className="post-owner-buttons">
            <button type="button" className="danger" onClick={() => requireLogin(() => onPostDelete(post))}>Delete</button>
          </div>
        </div>
      )}
      {post.flipCount > 0 && (
        <div className="flip-banner" onClick={e => e.stopPropagation()}>
          This Hix flipped {post.flipCount} time{post.flipCount === 1 ? '' : 's'}
        </div>
      )}
      {voted && (
        <div className="result-section" onClick={e => e.stopPropagation()}>
          <SplitBar pctA={pctA} />
          <div className="result-nums">
            <span className="red-text">{pctA}% {post.sideA}</span>
            <span className="blue-text">{pctB}% {post.sideB}</span>
          </div>
          <div className={`verdict ${isMajority ? 'majority' : 'minority'}`}>
            {isMajority ? `Majority - ${userPct}%` : `Minority - ${userPct}% - defend it`}
          </div>
        </div>
      )}
      <div className="vote-row" onClick={e => e.stopPropagation()}>
        <button className={`vote-btn red-btn ${voted === 'a' ? 'picked' : ''} ${voted && voted !== 'a' ? 'faded' : ''} ${isLocking ? 'loading' : ''}`} disabled={isLocking || Boolean(voted)} onClick={() => (isLocking || voted) ? null : onVote(post.id, 'a')}>
          <span>{post.sideA}</span>{voted && <strong>{pctA}%</strong>}
        </button>
        <button className={`vote-btn blue-btn ${voted === 'b' ? 'picked' : ''} ${voted && voted !== 'b' ? 'faded' : ''} ${isLocking ? 'loading' : ''}`} disabled={isLocking || Boolean(voted)} onClick={() => (isLocking || voted) ? null : onVote(post.id, 'b')}>
          <span>{post.sideB}</span>{voted && <strong>{pctB}%</strong>}
        </button>
      </div>
      <div className="comment-strip">
        <button
          className={`heat-action ${heated ? 'heated' : ''}`}
          onClick={(e) => { e.stopPropagation(); requireLogin(() => onHeat(post.id)); }}
          aria-label={heated ? 'Remove heat from this Hix' : 'Heat this Hix'}
          title={heated ? 'Remove heat' : 'Heat this Hix'}>
          <span className="heat-fire">&#128293;</span><span className="heat-label">Heat</span><strong>{heatCount}</strong>
        </button>
        <button className="comment-open-btn" onClick={(e) => { e.stopPropagation(); onOpen(post); }} aria-label={`Open ${commentTotal} ${getCommentLabel(commentTotal)}`} title={`${commentTotal} ${getCommentLabel(commentTotal)}`}>
          <CommentBubbleIcon />
          <strong>{commentTotal}</strong>
          <span className="sr-only">{getCommentLabel(commentTotal)}</span>
        </button>
      </div>
    </div>
  );
}

export function RightPanel({ posts, onOpen, onExploreTopics }) {
  const sorted = [...posts].sort((a, b) => getHotScore(b) - getHotScore(a)).slice(0, 5);
  const topicCounts = posts.reduce((acc, post) => {
    const label = getTopicLabel(post.topic);
    acc[label] = (acc[label] || 0) + 1;
    return acc;
  }, {});
  const topTopics = Object.entries(topicCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8);

  return (
    <aside className="right-panel">
      <div className="rp-section">
        <div className="rp-title">Trending Hixs</div>
        {posts.length === 0
          ? [1,2,3].map(i => <SkeletonCard key={i} />)
          : sorted.map((post, i) => (
          <div key={post.id} className="rp-item" onClick={() => onOpen(post)}>
            <div className="rp-rank" style={{ color: i === 0 ? '#FF3B4E' : i === 1 ? '#FF7A00' : '#52516B' }}>
              #{i + 1}
            </div>
            <div className="rp-info">
              <div className="rp-q">{post.question}</div>
              <div className="rp-meta">{post.totalVotes || 0} lock-ins · {post.heat || 50}% split</div>
              <SplitBar pctA={getPostSplit(post).pctA} className="rp-split-bar" />
            </div>
          </div>
        ))}
      </div>
      <div className="rp-section">
        <div className="rp-title">Top Topics</div>
        {topTopics.length === 0
          ? <div className="breaking-empty">Topics appear as people create Hixs.</div>
          : (
            <>
              <div className="topic-cloud">
                {topTopics.map(([topic, count]) => (
                  <span key={topic}>{topic}<small>{count}</small></span>
                ))}
              </div>
              <button type="button" className="rp-view-all" onClick={onExploreTopics}>View all topics</button>
            </>
          )}
      </div>
    </aside>
  );
}

export function HotScreen({ posts, onOpen }) {
  const [hotMode, setHotMode] = useState('hot');
  const sorted = sortHotPosts(posts, hotMode);
  return (
    <div className="screen-wrap">
      <div className="hot-wrap">
        <div className="screen-header hot-header">
          <div>
            <div className="create-kicker">Heat Index</div>
            <div className="screen-title">Live Hix rankings</div>
            <div className="screen-sub">Most split, heated, and discussed right now.</div>
          </div>
          <div className="hot-summary">{sorted.length} active</div>
        </div>
        <div className="hot-filter-tabs" role="tablist" aria-label="Hot Hix ranking filters">
          {HOT_FILTERS.map(filter => (
            <button
              key={filter.id}
              type="button"
              className={hotMode === filter.id ? 'active' : ''}
              onClick={() => setHotMode(filter.id)}
              role="tab"
              aria-selected={hotMode === filter.id}>
              {filter.label}
            </button>
          ))}
        </div>
        <div className="hot-list">
          {sorted.length === 0
            ? [1,2,3].map(i => <SkeletonCard key={i} />)
            : sorted.map((post, i) => {
              const reason = getHotReason(post, hotMode);
              const thought = isOpenThought(post);
              if (thought) {
                return (
                  <div key={post.id} className="hot-card hix-theme-thought hot-thought-card" onClick={() => onOpen(post)}>
                    <div className={`hot-rank-media ${post.image ? 'has-image' : ''}`}>
                      {post.image ? (
                        <>
                          <img src={post.image} alt="" loading="lazy" />
                          <span>#{i + 1}</span>
                        </>
                      ) : (
                        <strong>#{i + 1}</strong>
                      )}
                    </div>
                    <div className="hot-info">
                      <div className="hot-topline">
                        <span className="post-topic" style={getTopicStyle(post.topic)}>{getTopicLabel(post.topic)}</span>
                        <span className="post-type-badge type-thought">Open Thought</span>
                        <span className={`hot-reason-badge reason-${getHotReasonClass(reason)}`}>{reason}</span>
                      </div>
                      <div className="hot-question">{post.question}</div>
                      <div className="hot-meta">{getTotalComments(post.comments || [])} {getCommentLabel(getTotalComments(post.comments || []))} - {post.heatCount || 0} heat</div>
                    </div>
                  </div>
                );
              }
              return (
            <div key={post.id} className={`hot-card hix-theme-${getPostTypeOption(post.postType).key}`} onClick={() => onOpen(post)}>
              <div className={`hot-rank-media ${post.image ? 'has-image' : ''}`}>
                {post.image ? (
                  <>
                    <img src={post.image} alt="" loading="lazy" />
                    <span>#{i + 1}</span>
                  </>
                ) : (
                  <strong>#{i + 1}</strong>
                )}
              </div>
              <div className="hot-info">
                <div className="hot-topline">
                  <span className="post-topic" style={getTopicStyle(post.topic)}>{getTopicLabel(post.topic)}</span>
                  <span className={`hot-reason-badge reason-${getHotReasonClass(reason)}`}>{reason}</span>
                  <span className={`post-type-badge type-${getPostTypeOption(post.postType).key}`}>{getPostTypeOption(post.postType).label}</span>
                  <span className="post-heat">Split {post.heat || 50}%</span>
                </div>
                <div className="hot-question">{post.question}</div>
                <div className="hot-meta">{post.totalVotes || 0} lock-ins - {getTotalComments(post.comments || [])} {getCommentLabel(getTotalComments(post.comments || []))} - {post.heatCount || 0} heat</div>
                <SplitBar pctA={getPostSplit(post).pctA} className="hot-split-bar" />
                <div className="hot-nums">
                  <span>{getPostSplit(post).pctA}% {post.sideA}</span>
                  <span>{getPostSplit(post).pctB}% {post.sideB}</span>
                </div>
              </div>
            </div>
          );
            })}
        </div>
      </div>
    </div>
  );
}
// ── CREATE SCREEN ────────────────────────────────────
